import {useForm} from 'react-hook-form'
import {useTranslation} from '@/translations/i18n.tsx'
import {zodResolver} from '@hookform/resolvers/zod'
import {Assignment} from '@features/assignment/types.ts'
import {useUpdatePersonalNotes} from '@features/assignment/queries/useUpdatePersonalNotes'
import {FC, useMemo} from 'react'
import {Button} from '@components/ui/button'
import {Spinner} from '@components/ui/spinner'
import {z} from 'zod'
import {getServiceType} from '@features/assignment/utils.tsx'
import {TextArea} from '@components/commons/textarea'
import {toast} from '@utilities/toast'
import {noUnsupportedChars} from '@utilities/helpers'

const TEXTAREA_MAX_LENGTH = 2000

const PersonalNotesValidationSchema = z.object({
    notes: noUnsupportedChars(z.string().max(TEXTAREA_MAX_LENGTH))
})

export const AssignmentNotesCardForm: FC<{assignment: Assignment}> = ({assignment}) => {
    const {t} = useTranslation()

    const form = useForm({
        defaultValues: {notes: assignment.notes ?? ''},
        mode: 'onChange',
        resolver: zodResolver(PersonalNotesValidationSchema)
    })

    const updatePersonalNotes = useUpdatePersonalNotes({
        assignmentId: assignment.id,
        assignmentCode: assignment.code,
        options: {
            onSuccess: data => {
                toast.success(t('assignment:notes_card:success'))
                form.reset({notes: data.notes ?? ''})
            }
        }
    })

    const onSubmit = form.handleSubmit(data => {
        updatePersonalNotes.mutate(data.notes)
    })

    const isWalkthrough = useMemo(
        () => getServiceType(assignment.task_media.name, assignment.task_category.name) == 'walkthrough',
        [assignment.task_media, assignment.task_category]
    )

    return (
        <form className="flex gap-2 flex-col items-end" onSubmit={onSubmit}>
            <TextArea
                placeholder={t('assignment:notes_card:placeholder')}
                maxLength={TEXTAREA_MAX_LENGTH}
                errorMessage={form.formState.errors.notes?.message}
                rows={4}
                helpText={
                    <div className="flex flex-col gap-2 items-end">
                        <p>
                            {t('commons:x_characters_available', {
                                count: TEXTAREA_MAX_LENGTH - form.watch('notes').length
                            })}
                        </p>
                        <p>
                            {isWalkthrough
                                ? t('assignment:notes_card:walkthrough_hint')
                                : t('assignment:notes_card:hint')}
                        </p>
                    </div>
                }
                {...form.register('notes')}
            />

            <div className="flex justify-end gap-2">
                <Button
                    variant="tertiary"
                    type="button"
                    onClick={() => form.reset()}
                    disabled={updatePersonalNotes.isPending || !form.formState.isDirty}
                >
                    {t('commons:cancel')}
                </Button>
                <Button
                    variant="secondary"
                    type="submit"
                    disabled={updatePersonalNotes.isPending || !form.formState.isDirty}
                >
                    {updatePersonalNotes.isPending && <Spinner size={20} />}
                    {t('assignment:save')}
                </Button>
            </div>
        </form>
    )
}
